import { useMemo, useState } from 'react';
import type { FieldArrayWithId } from 'react-hook-form';

import { DataTable } from 'components/DataTable';
import { SectionContainer } from 'components/DetailsView/Section';
import { DetailValue } from 'components/DetailsView';

import { formatEuroAmount } from 'helpers/price';

import { GetProductsToOrderOnceForSupplyOrderQuery, SupplyOrderState } from 'data/__generated__';

import { getOrderedProductsColumns, OrderedProductRow } from '../constants/orderedProductsColumns';
import { SupplyOrderDetails, SupplyOrderFormValues } from '../supplyOrderFormValues';
import { ConfirmedQuantityHistoryPopup } from '../Popups/ConfirmedQuantityHistoryPopup';
import { SupplyOrderProductInfoReceivedDetailsPopup } from '../Popups/SupplyOrderProductInfoReceivedDetailsPopup';
import { useSupplyOrderStockVariations } from '../StockVariationPreviewCell';

/**
 * Les produits commandés, et eux seuls.
 *
 * L'ancienne version fusionnait ici le catalogue entier du fournisseur : une ligne sur cinq portait une
 * quantité, et vingt-et-une colonnes obligeaient à scroller pour lire une quantité. Ce qu'il reste à
 * commander vit maintenant dans sa propre section, chargée à la demande.
 */
export const OrderedProductsSection = ({
    supplyOrder,
    productsToOrderOnce,
    productFields,
}: {
    supplyOrder: SupplyOrderDetails;
    productsToOrderOnce: GetProductsToOrderOnceForSupplyOrderQuery['productsToOrderOnceForSupplyOrder'];
    // Fourni par la page : un seul `useFieldArray` pour toute la fiche (voir SupplyOrderDetailsForm).
    productFields: FieldArrayWithId<SupplyOrderFormValues, 'products', 'id'>[];
}) => {
    const [receivedDetailsIndex, setReceivedDetailsIndex] = useState<number | null>(null);
    const [confirmedQuantityHistoryProductId, setConfirmedQuantityHistoryProductId] = useState<string | null>(null);

    // Uniquement entre l'arrivée et la vérification : une fois vérifié, les quantités reçues ont validé le
    // bon de réception Odoo et alimenté le rangement — l'API refuse de les modifier.
    const canEditReceivedDetails = supplyOrder.state === SupplyOrderState.Received;

    const minQuantityByProductId = useMemo(
        () =>
            new Map(
                supplyOrder.supplier.orderingProductTerms.map(({ productId, minQuantity }) => [productId, minQuantity]),
            ),
        [supplyOrder.supplier.orderingProductTerms],
    );
    const productIdsToOrderOnce = useMemo(
        () => new Set(productsToOrderOnce.map(({ _id }) => _id)),
        [productsToOrderOnce],
    );
    const stockVariations = useSupplyOrderStockVariations(supplyOrder._id);
    const confirmedQuantityHistoryByProductId = useMemo(
        () => new Map(supplyOrder.products.map(({ _id, confirmedQuantityHistory }) => [_id, confirmedQuantityHistory])),
        [supplyOrder.products],
    );

    // `fields` suit l'ordre du formulaire ; l'index de ligne EST l'index de formulaire, y compris pour
    // les produits ajoutés depuis « Produits à commander ». Le tri du tableau ne le change pas : la
    // DataTable trie ses lignes, pas le tableau de formulaire.
    const rows: OrderedProductRow[] = productFields.map((field, formIndex) => ({ ...field, formIndex }));

    const columns = useMemo(
        () =>
            getOrderedProductsColumns({
                canEditReceivedDetails,
                minQuantityByProductId,
                productIdsToOrderOnce,
                onEditReceivedDetails: setReceivedDetailsIndex,
                onShowConfirmedQuantityHistory: setConfirmedQuantityHistoryProductId,
                confirmedQuantityHistoryByProductId,
                stockVariations,
                orderingTimes: supplyOrder.supplier.orderingTimes,
            }),
        [
            canEditReceivedDetails,
            minQuantityByProductId,
            productIdsToOrderOnce,
            confirmedQuantityHistoryByProductId,
            stockVariations,
            supplyOrder.supplier.orderingTimes,
        ],
    );

    const orderedLinesCount = rows.filter(({ colis, confirmedQuantity }) => colis || confirmedQuantity).length;

    return (
        <SectionContainer title={`Produits commandés (${orderedLinesCount})`} isInitiallyOpen>
            <>
                <div className="flex flex-col gap-3" data-test="supply-order-products">
                    <div className="flex flex-wrap gap-6">
                        <DetailValue
                            isValid={supplyOrder.orderingTermsValidity.isValid}
                            label="Coût de la livraison"
                            value={
                                supplyOrder.orderingTermsValidity.isValid
                                    ? formatEuroAmount(supplyOrder.orderingTermsValidity.deliveryPrice)
                                    : 'Conditions de commande non remplies'
                            }
                        />
                        <DetailValue label="Poids total" value={`${supplyOrder.totalWeight / 1000} kg`} />
                        <DetailValue label="Volume total" value={`${supplyOrder.totalVolume} cm³`} />
                    </div>
                    {/* Hauteur libre : un achat tient en quelques dizaines de lignes, on les affiche toutes
                        plutôt que d'imposer un second scroll au milieu de la fiche. */}
                    <div className="w-full">
                        <DataTable<OrderedProductRow>
                            autoHeight
                            columns={columns}
                            data={rows}
                            filterMode="client"
                            disableUrlSync
                            autoFocusSearch={false}
                            getRowId={(row) => row._id}
                            searchPlaceholder="Rechercher un produit…"
                            emptyMessage="Aucun produit sur cet achat. Ajoutez-en depuis « Produits à commander »."
                        />
                    </div>
                </div>

                {receivedDetailsIndex !== null ? (
                    <SupplyOrderProductInfoReceivedDetailsPopup
                        isOpen
                        setIsOpen={() => setReceivedDetailsIndex(null)}
                        productIndex={receivedDetailsIndex}
                    />
                ) : null}
                {confirmedQuantityHistoryProductId ? (
                    <ConfirmedQuantityHistoryPopup
                        isOpen
                        setIsOpen={() => setConfirmedQuantityHistoryProductId(null)}
                        quantityHistoryValues={
                            confirmedQuantityHistoryByProductId.get(confirmedQuantityHistoryProductId) ?? []
                        }
                    />
                ) : null}
            </>
        </SectionContainer>
    );
};
